import React, { useCallback, useRef } from 'react'

import { GlobalModalProps } from 'app/GlobalModalContext'

import useModalToggle, { useModalDeclaration } from 'v2/ui/utils/useModalToggle'

export type GlobalModalState<D> = {
    isOpen: boolean
    data: D | undefined
    onOpenChange: (open: boolean) => void
}

export type GlobalModalOverrides<D> = Partial<GlobalModalState<D>>

export type GlobalModal<D extends unknown, P> = React.FC<P & Partial<GlobalModalState<D>>>

/**
 * Use this hook to register a modal component that will be shown globally.
 * This hook doesn't react to component changes.
 * @param component
 * @returns
 */
export function useGlobalModal<C>(
    component: C extends GlobalModal<infer _D, infer _P> ? C : never
) {
    type Data = C extends GlobalModal<infer D, infer _P> ? D : never

    const componentRef = useRef<C>(component)
    componentRef.current = component

    const key = useRef(component.name)
    const { show: showModal, setIsOpen } = useModalDeclaration<Data>(
        key.current,
        component as unknown as React.ComponentType<GlobalModalProps>
    )

    const show = useCallback(
        (data: Data) => {
            showModal(data)
        },
        [showModal]
    )

    const close = useCallback(() => {
        setIsOpen(false)
    }, [setIsOpen])

    return {
        show,
        close,
    }
}

export function useGlobalModalState<C>(
    component: C extends GlobalModal<infer _D, infer _P> ? C : never,
    overrides?: GlobalModalOverrides<C extends GlobalModal<infer D, infer _P> ? D : never>
) {
    type Data = C extends GlobalModal<infer D, infer _P> ? D : never

    const key = useRef(component.name)
    const {
        isOpen,
        setIsOpen,
        data,
        show: showModal,
    } = useModalToggle<Data | undefined>(key.current)

    const effectiveIsOpen = overrides?.isOpen ?? isOpen

    const effectiveData = overrides?.data ?? data
    const effectiveDataRef = useRef(effectiveData)
    effectiveDataRef.current = effectiveData

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                showModal(effectiveDataRef.current)
            } else {
                setIsOpen(false)
            }
            overrides?.onOpenChange?.(open)
        },
        [setIsOpen, showModal, overrides]
    )

    return {
        isOpen: effectiveIsOpen,
        onOpenChange,
        data: effectiveData,
    }
}
